import React, { useState } from 'react';
import { Mail, Phone, MapPin, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import PageBanner from '../components/PageBanner';
import Footer from '../components/Footer';
import { submitContact } from '../lib/api';
import { IMAGES } from '../mock';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success('Message sent! We will get back to you soon.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[15px] focus:outline-none focus:border-[#4a9cd4] transition-colors';

  return (
    <>
      <PageBanner
        title="Contact Us"
        crumb="CONTACT"
        subtitle="Have a question or want to get involved? We would love to hear from you."
        image={IMAGES.heroB}
      />
      <section className="bg-[#f8f6f2] py-16 md:py-24">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <p className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] font-500 mb-4">GET IN TOUCH</p>
            <h2 className="font-serif-ah text-[36px] md:text-[44px] text-[#2f2f2f] leading-tight mb-8">We're here to help</h2>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#eef4f8] flex items-center justify-center shrink-0"><Mail size={19} className="text-[#4a9cd4]" /></span>
                <div>
                  <p className="font-sans-ah text-[12px] tracking-wide-ah text-[#999] uppercase">Email</p>
                  <a href="mailto:almaguinhighlandschamber@gmail.com" className="font-sans-ah text-[15px] text-[#3a3a3a] hover:text-[#4a9cd4] break-all">almaguinhighlandschamber@gmail.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#eef4f8] flex items-center justify-center shrink-0"><Phone size={19} className="text-[#4a9cd4]" /></span>
                <div>
                  <p className="font-sans-ah text-[12px] tracking-wide-ah text-[#999] uppercase">Phone</p>
                  <p className="font-sans-ah text-[15px] text-[#3a3a3a]">(705) 384-5300</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-[#eef4f8] flex items-center justify-center shrink-0"><MapPin size={19} className="text-[#4a9cd4]" /></span>
                <div>
                  <p className="font-sans-ah text-[12px] tracking-wide-ah text-[#999] uppercase">Location</p>
                  <p className="font-sans-ah text-[15px] text-[#3a3a3a]">Almaguin Highlands, Ontario, Canada</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3 bg-white rounded-sm p-8 md:p-10 shadow-[0_10px_50px_rgba(0,0,0,0.07)] border border-neutral-100">
            {done ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-full bg-[#eef4f8] flex items-center justify-center mx-auto mb-6"><Check size={38} className="text-[#4a9cd4]" /></div>
                <h3 className="font-serif-ah text-[30px] text-[#2f2f2f] mb-3">Message sent!</h3>
                <p className="font-sans-ah text-[15px] text-[#666] font-300">Thanks for reaching out. We'll get back to you as soon as we can.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Name *</label>
                    <input id="contact-name" name="name" data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set('name')} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Email *</label>
                    <input id="contact-email" name="email" data-testid="contact-email-input" type="email" className={inputCls} value={form.email} onChange={set('email')} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-phone" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Phone</label>
                    <input id="contact-phone" name="phone" data-testid="contact-phone-input" className={inputCls} value={form.phone} onChange={set('phone')} />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Subject</label>
                    <input id="contact-subject" name="subject" data-testid="contact-subject-input" className={inputCls} value={form.subject} onChange={set('subject')} />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="font-sans-ah text-[12px] tracking-wide-ah text-[#666] uppercase">Message *</label>
                  <textarea id="contact-message" name="message" data-testid="contact-message-input" rows={5} className={inputCls + ' resize-none'} value={form.message} onChange={set('message')} />
                </div>
                <button type="submit" data-testid="contact-submit-btn" disabled={submitting} className="btn-ah-solid w-full font-sans-ah text-[13px] py-4 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  {submitting ? 'SENDING...' : 'SEND MESSAGE'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
